import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DiaServicio, Organo, ServicioItem, ServicioService } from './servicio.service';
import { AuthService } from '../../commons/services/auth.service';

interface ServicioView extends ServicioItem {
  dias: DiaServicio[];
}

@Component({
  selector: 'oaj-servicio-itx',
  standalone: true,
  templateUrl: './servicio.component.html',
  styleUrls: ['./servicio.component.css'],
  imports: [CommonModule, FormsModule],
})
export class ServicioComponent implements OnInit, OnDestroy {
  servicios: ServicioView[] = [];
  loading = false;
  savingId?: number;
  deletingId?: number;
  errorMessage = '';
  successMessage = '';
  organos: Organo[] = [];
  selectedOrganismoId: number | null = null;
  organosLoading = false;
  organosError = '';
  collapsedState: Record<number, boolean> = {};
  private successTimeoutId?: number;

  private servicioService: ServicioService = inject(ServicioService);
  private authService: AuthService = inject(AuthService);


  ngOnInit(): void {
    this.cargarOrganos();
    this.buscarServicios();
  }

  ngOnDestroy(): void {
    this.clearSuccessMessage();
  }

  buscarServicios(): void {
    this.cargarServicios(this.selectedOrganismoId);
  }

  /**
   * Función para agregar una nueva configuración de servicio, se valida que se haya seleccionado un organismo, se construye un nuevo objeto de servicio con los datos por defecto y se agrega a la lista de servicios para su edición, si no se ha seleccionado un organismo se muestra un mensaje de error
   * @returns 
   */
  agregarConfiguracion(): void {
    this.clearSuccessMessage();
    this.errorMessage = '';
    if (this.selectedOrganismoId === null || this.selectedOrganismoId === undefined) {
      this.errorMessage = 'Selecciona un organo del catalogo para agregar configuracion.';
      return;
    }

    const tipoOrganismoId = this.getIdTypeCourt();
    const nowIso = new Date().toISOString();
    const nuevo: ServicioView = {
      kIdOperServicio: this.selectedOrganismoId,
      fCatTipoOrganismoId: tipoOrganismoId,
      fCatOrganismoId: this.selectedOrganismoId,
      sConfiguracion: '',
      fFechaAlta: nowIso,
      fFechaCambio: nowIso,
      fFechaBaja: null,
      bEstatus: true,
      dias: this.obtenerDiasPorDefecto(),
    };

    this.servicios = [nuevo];
    this.savingId = undefined;
    this.deletingId = undefined;
  }

  /**
   * Función para cargar los servicios, se recibe el ID del organismo y se consulta el servicio para obtener los datos correspondientes
   * @param fCatOrganismoId 
   */
  cargarServicios(fCatOrganismoId: number | null = null): void {
    this.loading = true;
    this.errorMessage = '';
    this.servicioService.obtenerServicios(fCatOrganismoId).subscribe({
      next: (items) => {
        this.servicios = items.map((item) => ({
          ...item,
          dias: this.parseConfig(item.sConfiguracion),
        }));
        this.loading = false;
      },
      error: () => {
        this.errorMessage =
          'No fue posible recuperar la configuración de servicio. Intenta nuevamente.';
        this.loading = false;
      },
    });
  }

  /**
   * Función para mostrar el nombre del organismo asociado a un servicio
   * @param servicio 
   * @returns 
   */
  displayOrganismo(servicio: ServicioView): string {
    if (servicio.fCatOrganismoId === 0) {
      return 'Todos';
    }

    const organo = this.organos.find((o) => o.courtId === servicio.fCatOrganismoId);
    if (organo) {
      return `${organo.courtId} - ${organo.courtName}`;
    }
    return `Organismo ${servicio.fCatOrganismoId}`;
  }

  isTodos(servicio: ServicioView): boolean {
    return servicio.fCatOrganismoId === 0;
  }

  toggleCollapse(servicio: ServicioView): void {
    const id = servicio.kIdOperServicio;
    this.collapsedState[id] = !this.isCollapsed(servicio);
  }

  isCollapsed(servicio: ServicioView): boolean {
    return this.collapsedState[servicio.kIdOperServicio] ?? false;
  }

  /**
   * Función para cargar el catálogo de órganos, se consulta el servicio para obtener la lista de órganos disponibles, si la consulta es exitosa se asignan los órganos a la propiedad correspondiente, si ocurre un error durante la consulta se muestra un mensaje de error
   * @returns
   */
  private cargarOrganos(): void {
    this.organosLoading = true;
    this.organosError = '';
    this.servicioService.obtenerOrganos().subscribe({
      next: (organos) => {
        this.organos = organos ?? [];
        this.organosLoading = false;
      },
      error: () => {
        this.organosError = 'No fue posible cargar el catalogo de organos.';
        this.organosLoading = false;
      },
    });
  }

  /**
   * Función para guardar la configuración de un servicio, se recibe el objeto de servicio y se llama al servicio para realizar el guardado
   * @param servicio 
   */
  guardar(servicio: ServicioView): void {
    this.clearSuccessMessage();
    this.errorMessage = '';
    this.savingId = servicio.fCatOrganismoId;

    const dias = this.normalizarDias(servicio.dias);
    this.servicioService
      .guardarDias(servicio.fCatOrganismoId, servicio.fCatTipoOrganismoId, dias)
      .subscribe({
        next: () => {
          this.setSuccessMessage('Configuración guardada correctamente.');
          this.savingId = undefined;
        },
        error: () => {
          this.errorMessage = 'Ocurrió un error al guardar. Intenta nuevamente.';
          this.clearSuccessMessage();
          this.savingId = undefined;
        },
      });
  }

  /**
   * Función para desactivar un servicio, se recibe el objeto de servicio y se llama al servicio para realizar la desactivación
   * @param servicio 
   */
  desactivar(servicio: ServicioView): void {
    this.errorMessage = '';
    this.clearSuccessMessage();
    this.deletingId = servicio.kIdOperServicio;
    this.servicioService.desactivar(servicio.kIdOperServicio).subscribe({
      next: () => {
        this.servicios = this.servicios.filter(
          (s) => s.kIdOperServicio !== servicio.kIdOperServicio
        );
        this.setSuccessMessage('Servicio desactivado.');
        this.deletingId = undefined;
      },
      error: () => {
        this.errorMessage = 'No fue posible desactivar el servicio.';
        this.clearSuccessMessage();
        this.deletingId = undefined;
      },
    });
  }

  toTimeInput(value: string | undefined): string {
    const normalized = this.normalizarHora(value);
    const [h, m] = normalized.split(':');
    return `${h}:${m}`;
  }

  onTimeChange(dia: DiaServicio, campo: 'HoraInicio' | 'HoraFin', value: string): void {
    dia[campo] = this.normalizarHora(value);
  }

  onToggle(
    dia: DiaServicio,
    campo: 'Laborable' | 'Completo',
    checked: boolean
  ): void {
    dia[campo] = checked;
  }

  private parseConfig(config: string): DiaServicio[] {
    if (!config) {
      return this.obtenerDiasPorDefecto();
    }

    try {
      const parsed = JSON.parse(config);
      const dias =
        parsed?.Servicio?.Dias ??
        parsed?.diasServicio ??
        parsed?.Dias ??
        parsed?.Servicio?.diasServicio ??
        [];
      if (Array.isArray(dias)) {
        return this.normalizarDias(dias as DiaServicio[]);
      }
    } catch (error) {
      console.warn('No se pudo deserializar sConfiguracion', error);
    }

    return this.obtenerDiasPorDefecto();
  }

  private normalizarDias(dias: DiaServicio[]): DiaServicio[] {
    return dias.map((d) => ({
      Dia: d.Dia ?? '',
      Completo: this.toBoolean(d.Completo),
      HoraInicio: this.normalizarHora(d.HoraInicio),
      HoraFin: this.normalizarHora(d.HoraFin),
      Laborable: this.toBoolean(d.Laborable),
    }));
  }

  toBoolean(value: boolean | string | number | undefined | null): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return false;
  }

  private normalizarHora(value: string | undefined | null): string {
    if (!value) {
      return '00:00:00';
    }
    const partes = value.split(':');
    const hora = partes[0]?.padStart(2, '0') || '00';
    const minuto = partes[1]?.padStart(2, '0') || '00';
    return `${hora}:${minuto}:00`;
  }

  private obtenerDiasPorDefecto(): DiaServicio[] {
    const dias = [
      'Lunes',
      'Martes',
      'Miercoles',
      'Jueves',
      'Viernes',
      'Sabado',
      'Domingo',
    ];
    return dias.map((dia) => ({
      Dia: dia,
      Completo: false,
      HoraInicio: '09:00:00',
      HoraFin: '18:00:00',
      Laborable: !['Sabado', 'Domingo'].includes(dia),
    }));
  }

  private setSuccessMessage(message: string): void {
    this.clearSuccessMessage();
    this.successMessage = message;
    this.successTimeoutId = window.setTimeout(() => {
      this.successMessage = '';
      this.successTimeoutId = undefined;
    }, 3000);
  }

  private clearSuccessMessage(): void {
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
      this.successTimeoutId = undefined;
    }
    this.successMessage = '';
  }

  private getIdTypeCourt(): number {
    return this.authService.getIdTypeCourt() ?? 0;
  }

}
